'use client';
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from '@floating-ui/react';
import c from 'classnames';
import { CaretDown, CaretUp } from 'phosphor-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatTitleForID } from 'utils';
import { c as cm } from '../../../utils/tailwind-merge';
import { FormErrorMessage } from '../../Form/FormErrorMessage';

export type DropdownOption = {
  label: React.ReactNode;
  value: string;
};

export type DropdownProps = {
  id: string;
  options: DropdownOption[];
  selectedOption: DropdownOption | null;
  onSelect: (option: DropdownOption) => void;
  isDarkTheme?: boolean;
  /**
   * aria tags should be used to provide extra context to screen reader users about functionality of the popup
   * ariaLabelledBy: if there is another visible element on the page that acts as a label for the dropdown, use element id(s)
   */
  ariaLabelledBy?: string;
  /**
   * ariaLabel: if there is no visible label that provides context for the dropdowns functionality, can use a text description.
   * Be aware that changing this will override whatever content is in the button, so you should make sure to use the Dropdown's value as part of the label.
   */
  ariaLabel?: string;
  /* If its possible to have no value selected, provide a placeholder */
  noSelectedOptionPlaceholder?: string;
  /* maximum height in pixels for the dropdown, content will scroll if it goes past */
  maxDropdownHeight?: number;
  dropdownStyle?: 'default' | 'newsletter';
  className?: string;
  buttonClassName?: string;
  error?: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
};

const buttonClassNames = (
  isOpen: boolean,
  dropdownStyle: string,
  isDarkTheme: boolean
) => {
  const isNewsletter = dropdownStyle === 'newsletter';

  return c(
    'flex items-center justify-between py-2 pl-3 pr-2 gap-x-2 w-full border font-medium focus-outline rounded-lg disabled:pointer-events-none disabled:text-grey-400',
    isNewsletter ? 'bg-transparent body-small' : 'text-sm',
    isDarkTheme
      ? [
          'border-grey-400',
          {
            'text-white hover:text-grey-400': isNewsletter,
            'bg-grey-900 text-white': !isNewsletter && isOpen,
            'bg-grey-800 text-grey-400 hover:text-grey-300':
              !isNewsletter && !isOpen,
          },
        ]
      : [
          'border-line-strong',
          {
            'text-content-subtle hover:text-content-muted': !isOpen,
            'dark:text-content dark:hover:text-content-subtle dark:disabled:text-grey-400':
              isNewsletter,
            'bg-surface text-content-muted': !isNewsletter && isOpen,
            'bg-surface-muted': !isNewsletter && !isOpen,
          },
        ]
  );
};

const optionClassNames = (isSelected: boolean) =>
  c(
    'cursor-pointer block focus-outline rounded-md m-1 p-2 text-sm truncate transition',
    {
      'font-bold text-black dark:text-content bg-surface-muted': isSelected,
      'text-content-muted font-medium hover:text-black dark:hover:text-content bg-surface-raised hover:bg-surface-strong':
        !isSelected,
    }
  );

const scrollbarStyles = `
  /* Firefox, Chrome */
  .dropdown-listbox {
    scrollbar-width: thin;
    scrollbar-color: rgb(var(--color-line)) rgb(var(--color-surface-raised));
  }

  /* Safari */
  .dropdown-listbox::-webkit-scrollbar {
    width: 12px;
  }

  .dropdown-listbox::-webkit-scrollbar-track {
    background: rgb(var(--color-surface-raised));
  }

  .dropdown-listbox::-webkit-scrollbar-thumb {
    background-color: rgb(var(--color-line));
    border-radius: 20px;
    border: 3px solid rgb(var(--color-surface-raised));
  }
`;

export const Dropdown = ({
  id,
  options,
  selectedOption,
  onSelect,
  noSelectedOptionPlaceholder = '',
  ariaLabelledBy,
  ariaLabel,
  isDarkTheme = true,
  maxDropdownHeight,
  className = '',
  buttonClassName,
  dropdownStyle = 'default',
  error,
  label,
  isRequired = false,
  disabled = false,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => setIsOpen(!isOpen);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement[]>([]);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    // portaled listbox needs an explicit width to match the trigger
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });
  const [portalRoot, setPortalRoot] = useState<HTMLElement>();
  // a modal dialog (e.g. a Radix sheet) blocks pointer events and focus outside itself
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      refs.setReference(node);
      setPortalRoot(node?.closest<HTMLElement>('[role="dialog"]') ?? undefined);
    },
    [refs]
  );
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, listNavigation]
  );

  const handleSelect = (option: DropdownOption) => {
    if (disabled) return;
    toggleDropdown();
    onSelect(option);
  };

  const handleDropdownOptionKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    option: DropdownOption
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleSelect(option);
    } else if (e.key === 'Tab') {
      // close the dropdown when tabbing out
      toggleDropdown();
    }
  };

  // Scroll to selected option when dropdown opens, without animation
  useEffect(() => {
    if (isOpen && selectedOption) {
      const index = options.findIndex((o) => o.value === selectedOption.value);
      const el = listRef.current[index];
      const container = el?.parentElement;

      if (el && container) {
        const offsetTop = el.offsetTop;
        container.scrollTop = offsetTop;
      }
    }
  }, [isOpen, options, selectedOption]);

  useEffect(() => {
    if (isOpen && selectedOption) {
      const index = options.findIndex((o) => o.value === selectedOption.value);
      setActiveIndex(index !== -1 ? index : null);
    }
  }, [isOpen, selectedOption, options]);

  // `disabled` only reaches the trigger button; an already-open portaled menu
  // would otherwise stay actionable while the control reads as disabled.
  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const suffixId = `dropdown-${id}`;
  const selectedLabelId = `${suffixId}-option-label`;
  const labelId = `${suffixId}-label`;
  const errorId = `${suffixId}-error`;

  const describedBy = error ? errorId : undefined;
  const listboxLabelledBy = label ? labelId : ariaLabelledBy;

  let labelledby: string | undefined;
  if (label) {
    labelledby = `${labelId} ${selectedLabelId}`;
  } else if (ariaLabelledBy) {
    labelledby = `${ariaLabelledBy} ${selectedLabelId}`;
  }

  return (
    <div
      className={cm('relative flex w-full min-w-32 flex-col', className)}
      ref={dropdownRef}
    >
      {label && (
        <label
          htmlFor={suffixId}
          id={labelId}
          className={c('mb-1 font-semibold text-white', {
            'dark:text-content': !isDarkTheme,
          })}
        >
          {label}
        </label>
      )}
      <style>{scrollbarStyles}</style>
      <button
        id={suffixId}
        data-test={suffixId}
        ref={setTriggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${suffixId}-listbox` : undefined} // Active only when open
        // read out the aria-labelledby element's content as well as the currently selected option
        aria-labelledby={labelledby}
        aria-label={!label ? ariaLabel : undefined}
        aria-describedby={describedBy}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex !== null ? `${suffixId}-option-${activeIndex}` : undefined
        }
        aria-required={isRequired}
        disabled={disabled}
        className={cm(
          buttonClassNames(isOpen, dropdownStyle, isDarkTheme),
          buttonClassName
        )}
        // While seemingly unnecessary, this prop prevents the button from submitting any parent form element
        type="button"
        {...getReferenceProps()}
      >
        <span className="truncate" id={selectedLabelId}>
          {selectedOption ? selectedOption.label : noSelectedOptionPlaceholder}
        </span>
        {isOpen ? (
          <CaretUp size={15} className="pointer-events-none flex-shrink-0" />
        ) : (
          <CaretDown size={15} className="pointer-events-none flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <FloatingPortal root={portalRoot}>
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div
              // Extra wrapper div with overflow-hidden so that the rounded corners hide the corners of the scrollbar
              className="z-50 mt-1 overflow-hidden rounded-lg border border-line-strong shadow-lg"
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
              }}
              {...getFloatingProps()}
            >
              <div
                id={`${suffixId}-listbox`}
                role="listbox"
                className="dropdown-listbox w-full overflow-y-auto bg-surface-raised"
                style={{
                  maxHeight: maxDropdownHeight,
                }}
                aria-labelledby={listboxLabelledBy}
              >
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    id={`dropdown-${id}-option-${index}`}
                    data-test={`dropdown-${id}-option-${formatTitleForID(option.value)}`}
                    role="option"
                    aria-selected={selectedOption?.value === option.value}
                    tabIndex={activeIndex === index ? 0 : -1}
                    className={optionClassNames(
                      selectedOption?.value === option.value
                    )}
                    ref={(node) => {
                      if (node) {
                        listRef.current[index] = node;
                      }
                    }}
                    {...getItemProps()}
                    onClick={() => handleSelect(option)}
                    onKeyDown={(e) => handleDropdownOptionKeyDown(e, option)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
      {error && (
        <div id={errorId} aria-live="polite">
          <FormErrorMessage inputId={id} errorMessage={error} />
        </div>
      )}
    </div>
  );
};
